import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { DayPicker, useNavigation } from "react-day-picker";
import CoachMultiSelect from "./CoachMultiSelect";
import CreateClassSelect from "./CreateClassSelect";
import HintTooltip from "./HintTooltip";
import { fetchMembershipPlans } from "./membership-plans-fetch";
import {
  allWeekdaysSelected,
  WEEKDAYS,
  weekdayStateFromPython,
  weekdayStateToPython,
} from "./recurring-weekday-encoding";
import {
  BOOKING_CUTOFF_OPTIONS,
  normalizeBookingCutoff,
  normalizeClassBookingLimitFields,
} from "./scheduling-limit-period";

const USER_TYPES = [
  { id: "push", label: "PUSH (Programs only)" },
  { id: "pull", label: "PULL (1:1 Coaching)" },
];

const TIME_PERIOD_OPTIONS = [
  { id: "daily", label: "Daily" },
  { id: "weekly", label: "Weekly" },
  { id: "monthly", label: "Monthly" },
];

const MAX_SESSIONS_OPTIONS = Array.from({ length: 30 }, (_, i) => i + 1);

/** Duration options: 15–120 minutes in 15-minute steps, then 3–8 hours hourly */
const DURATION_OPTIONS = [
  15, 30, 45, 60, 75, 90, 105, 120, 180, 240, 300, 360, 420, 480,
];

function formatDurationLabel(minutes) {
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  const parts = [];
  if (h) parts.push(`${h} ${h === 1 ? "hr" : "hrs"}`);
  if (m) parts.push(`${m} ${m === 1 ? "min" : "mins"}`);
  return parts.join(" ");
}

const START_TIME_OPTIONS = Array.from({ length: 96 }, (_, i) => {
  const totalMinutes = i * 15;
  const h24 = Math.floor(totalMinutes / 60);
  const min = totalMinutes % 60;
  const h12 = h24 === 0 ? 12 : h24 > 12 ? h24 - 12 : h24;
  const ampm = h24 < 12 ? "AM" : "PM";
  const value = `${String(h24).padStart(2, "0")}:${String(min).padStart(2, "0")}`;
  const label = `${h12}:${String(min).padStart(2, "0")} ${ampm}`;
  return { value, label };
});

function parseDateField(s) {
  if (!s || typeof s !== "string") return undefined;
  const day = s.trim().slice(0, 10);
  const m = day.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (!m) return undefined;
  return new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]), 0, 0, 0, 0);
}

function normalizeTimeForInput(t) {
  if (!t || typeof t !== "string") return "09:00";
  const m = t.match(/^(\d{1,2}):(\d{2})/);
  if (!m) return "09:00";
  const h = Math.min(23, Math.max(0, Number(m[1])));
  return `${String(h).padStart(2, "0")}:${m[2]}`;
}

function CreateClassCalendarCaption(props) {
  const { goToMonth, nextMonth, previousMonth } = useNavigation();
  const displayMonth = props?.displayMonth ?? new Date();
  const monthLabel = `${displayMonth.toLocaleDateString("en-US", { month: "long" })}, ${displayMonth.getFullYear()}`;
  return (
    <div className="create-class-modal-calendar-caption">
      <button
        type="button"
        onClick={() => previousMonth && goToMonth(previousMonth)}
        className="create-class-modal-calendar-nav"
        aria-label="Previous month"
      >
        <img src="/img/arrow-square-left.svg" alt="" />
      </button>
      <span className="create-class-modal-calendar-title">{monthLabel}</span>
      <button
        type="button"
        onClick={() => nextMonth && goToMonth(nextMonth)}
        className="create-class-modal-calendar-nav"
        aria-label="Next month"
      >
        <img src="/img/arrow-square-right.svg" alt="" />
      </button>
    </div>
  );
}

const CreateClassModal = ({
  isOpen,
  onClose,
  onConfirm,
  initialRecord,
  initialScrollSection = null,
  showAccessPermissions = true,
  hasPressScheduling = false,
  hasPullScheduling = false,
  canPushUser = true,
  canPullUser = true,
}) => {
  const handleClose = (e) => {
    e?.preventDefault?.();
    onClose();
  };

  /** Builds the create-class payload for POST. Reads basic fields from form by id; access/booking from state. */
  const getCreateClassPayload = () => {
    const nameEl = document.getElementById("create-class-event-name");
    const descEl = document.getElementById("create-class-description");
    const startTimeEl = document.getElementById("create-class-start-time");
    const locationEl = document.getElementById("create-class-location");
    const capacityEl = document.getElementById("create-class-capacity");
    const durationEl = document.getElementById("create-class-duration");
    const recurrenceRadios = document.querySelectorAll(
      'input[name="create-class-recurrence"]'
    );
    const recurrence_type =
      Array.from(recurrenceRadios).find((r) => r.checked)?.value ?? "weekly";

    const allowed_user_types =
      pressEnabled && accessModeEffective === "user_type"
        ? visibleUserTypes.filter(({ id }) => userTypes[id]).map(({ id }) => id)
        : [];
    const allowed_membership_plans =
      pressEnabled && accessModeEffective === "membership"
        ? Object.keys(memberships).filter((id) => memberships[id])
        : [];

    return normalizeClassBookingLimitFields({
      name: nameEl?.value?.trim() ?? "",
      description: descEl?.value?.trim() ?? "",
      thumbnail_url: "",
      duration_minutes: durationEl ? Number(durationEl.value) || 60 : 60,
      capacity: capacityEl ? Number(capacityEl.value) || 0 : 0,
      recurrence_type,
      recurrence_start_date: startDate ? toYYYYMMDD(startDate) : "",
      recurrence_end_date: endDate ? toYYYYMMDD(endDate) : "",
      recurrence_weekdays:
        recurrence_type === "daily" ? weekdayStateToPython(weekdays) : null,
      start_time: startTimeEl?.value ?? "09:00",
      location: locationEl?.value?.trim() ?? "",
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      ...(selectedCoachIds.length > 0 ? { coach_ids: selectedCoachIds } : {}),

      is_restricted: pressEnabled,
      allowed_user_types: pressEnabled ? allowed_user_types : [],
      allowed_membership_plans: pressEnabled ? allowed_membership_plans : [],
      pull_only: pullActive,

      per_time_period: perTimePeriodEnabled,
      per_member: perMemberEnabled,
      booking_limit_time_period: perTimePeriodEnabled
        ? perTimePeriodPeriod
        : undefined,
      per_member_time_period: perMemberEnabled ? perMemberPeriod : undefined,
      max_sessions_per_time_period: perTimePeriodEnabled
        ? perTimePeriodMax
        : undefined,
      max_sessions_per_member_per_time_period: perMemberEnabled
        ? perMemberMax
        : undefined,
      booking_limit_enabled: perTimePeriodEnabled || perMemberEnabled,
      booking_limit_count: perMemberEnabled
        ? perMemberMax
        : perTimePeriodEnabled
          ? perTimePeriodMax
          : undefined,
      booking_limit_period: perMemberEnabled
        ? perMemberPeriod
        : perTimePeriodEnabled
          ? perTimePeriodPeriod
          : undefined,
      booking_cutoff: normalizeBookingCutoff(formBookingCutoff),

      permissions: {
        allowed_user_types: pressEnabled ? allowed_user_types : [],
        allowed_plan_ids: pressEnabled ? allowed_membership_plans : [],
      },
    });
  };

  const [submitError, setSubmitError] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleCreateClass = async (e) => {
    e?.preventDefault?.();
    if (restrictedNoSelection) {
      setSubmitError(
        accessModeEffective === "user_type"
          ? "Select at least one user type when Press Users is enabled."
          : "Select at least one membership plan when Press Users is enabled."
      );
      return;
    }
    if (typeof onConfirm !== "function") {
      handleClose(e);
      return;
    }
    setSubmitError(null);
    setIsSubmitting(true);
    try {
      const payload = getCreateClassPayload();
      if (thumbnailFile) {
        payload.thumbnail_url = "";
      } else if (
        typeof thumbnailPreview === "string" &&
        thumbnailPreview &&
        !thumbnailPreview.startsWith("blob:")
      ) {
        payload.thumbnail_url = thumbnailPreview;
      } else {
        payload.thumbnail_url = "";
      }
      await Promise.resolve(
        onConfirm(payload, { thumbnailFile: thumbnailFile || undefined })
      );
      handleClose(e);
    } catch (err) {
      const msg = err?.message ?? "";
      const friendly = msg.toLowerCase().includes("recurrence_start_date")
        ? "start date is required."
        : msg ||
          (initialRecord?._id || initialRecord?.id
            ? "Failed to save class"
            : "Failed to create class");
      setSubmitError(friendly);
    } finally {
      setIsSubmitting(false);
    }
  };

  const [startDate, setStartDate] = useState(undefined);
  const [endDate, setEndDate] = useState(undefined);
  const [openDatePicker, setOpenDatePicker] = useState(null);
  const [pressEnabled, setPressEnabled] = useState(false);
  const [pullEnabled, setPullEnabled] = useState(false);
  // Which panel restricts access: "user_type" or "membership" (mutually exclusive).
  const [accessMode, setAccessMode] = useState("user_type");
  const [userTypes, setUserTypes] = useState({ push: true, pull: false });
  const [plans, setPlans] = useState([]);
  const [memberships, setMemberships] = useState({});
  const [perTimePeriodEnabled, setPerTimePeriodEnabled] = useState(false);
  const [perMemberEnabled, setPerMemberEnabled] = useState(false);
  const [perTimePeriodPeriod, setPerTimePeriodPeriod] = useState("weekly");
  const [perMemberPeriod, setPerMemberPeriod] = useState("weekly");
  const [perTimePeriodMax, setPerTimePeriodMax] = useState(10);
  const [perMemberMax, setPerMemberMax] = useState(2);
  const startPickerRef = useRef(null);

  const showPressToggle =
    showAccessPermissions &&
    (hasPressScheduling || Boolean(initialRecord?.is_restricted));
  const showPullToggle = showAccessPermissions && hasPullScheduling;
  const pullActive = showPullToggle && pullEnabled;

  const visibleUserTypes = USER_TYPES.filter(
    ({ id }) => (id === "push" && canPushUser) || (id === "pull" && canPullUser)
  );

  const userTypeAvailable = visibleUserTypes.length > 0;
  const accessModeEffective = userTypeAvailable ? accessMode : "membership";

  const selectAccessMode = (mode) => {
    setAccessMode(mode);
    if (mode === "user_type") {
      setMemberships(Object.fromEntries(plans.map(({ id }) => [id, false])));
    } else {
      setUserTypes({ push: false, pull: false });
    }
  };

  const restrictedNoSelection =
    pressEnabled &&
    (accessModeEffective === "user_type"
      ? !visibleUserTypes.some(({ id }) => userTypes[id])
      : !Object.keys(memberships).some((id) => memberships[id]));
  const endPickerRef = useRef(null);
  const thumbnailInputRef = useRef(null);
  const accessSectionRef = useRef(null);
  const [thumbnailFile, setThumbnailFile] = useState(null);
  const [thumbnailPreview, setThumbnailPreview] = useState(null);
  const [selectedCoachIds, setSelectedCoachIds] = useState([]);
  const [formName, setFormName] = useState("");
  const [formDuration, setFormDuration] = useState("60");
  const [formStartTime, setFormStartTime] = useState("09:00");
  const [formCapacity, setFormCapacity] = useState("10");
  const [formBookingCutoff, setFormBookingCutoff] = useState("");
  const [dateRangeEnabled, setDateRangeEnabled] = useState(false);
  const [recurrenceType, setRecurrenceType] = useState("daily");
  const [weekdays, setWeekdays] = useState(allWeekdaysSelected);
  const toggleWeekday = (key) =>
    setWeekdays((prev) => ({ ...prev, [key]: !prev[key] }));
  const selectedWeekdayCount = WEEKDAYS.filter(
    ({ key }) => weekdays[key]
  ).length;
  const dailyNeedsWeekday =
    recurrenceType === "daily" && selectedWeekdayCount === 0;

  const isFormValid =
    formName.trim() !== "" &&
    Boolean(startDate) &&
    formDuration !== "" &&
    formStartTime !== "" &&
    Number(formCapacity) > 0 &&
    !dailyNeedsWeekday;

  const onThumbnailPick = (e) => {
    const file = e.target.files?.[0];
    if (!file || !file.type.startsWith("image/")) return;
    setThumbnailFile(file);
    setThumbnailPreview((prev) => {
      if (prev?.startsWith?.("blob:")) URL.revokeObjectURL(prev);
      return URL.createObjectURL(file);
    });
    e.target.value = "";
  };

  const toggleUserType = (id) => {
    setUserTypes((prev) => ({ ...prev, [id]: !prev[id] }));
  };
  const toggleMembership = (id) => {
    setMemberships((prev) => ({ ...prev, [id]: !prev[id] }));
  };
  const allMembershipsChecked =
    plans.length > 0 && Object.values(memberships).every(Boolean);
  const setAllMemberships = (checked) => {
    const next = {};
    for (const { id } of plans) next[id] = checked;
    setMemberships(next);
  };

  useEffect(() => {
    let cancelled = false;
    fetchMembershipPlans().then((data) => {
      if (!cancelled) setPlans(data);
    });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (!isOpen) return;
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prevOverflow;
    };
  }, [isOpen]);

  useEffect(() => {
    if (!openDatePicker) return;
    const handleClickOutside = (e) => {
      if (
        openDatePicker === "start" &&
        startPickerRef.current &&
        !startPickerRef.current.contains(e.target)
      ) {
        setOpenDatePicker(null);
      }
      if (
        openDatePicker === "end" &&
        endPickerRef.current &&
        !endPickerRef.current.contains(e.target)
      ) {
        setOpenDatePicker(null);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [openDatePicker]);

  const formatDate = (d) =>
    d
      ? d.toLocaleDateString("en-US", {
          month: "short",
          day: "numeric",
          year: "numeric",
        })
      : "";
  const toYYYYMMDD = (d) => {
    if (!d) return "";
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, "0");
    const day = String(d.getDate()).padStart(2, "0");
    return `${y}-${m}-${day}`;
  };

  useEffect(() => {
    if (!isOpen) return;
    const id = requestAnimationFrame(() => {
      if (initialRecord) {
        const r = initialRecord;
        const nameEl = document.getElementById("create-class-event-name");
        if (nameEl) nameEl.value = r.name ?? "";
        setFormName(r.name ?? "");
        const descEl = document.getElementById("create-class-description");
        if (descEl) descEl.value = r.description ?? "";
        const durEl = document.getElementById("create-class-duration");
        if (durEl) durEl.value = String(r.duration_minutes ?? 60);
        setFormDuration(String(r.duration_minutes ?? 60));
        const capEl = document.getElementById("create-class-capacity");
        if (capEl) capEl.value = String(r.capacity ?? 10);
        setFormCapacity(String(r.capacity ?? 10));
        setFormBookingCutoff(
          r.booking_cutoff == null ? "" : String(r.booking_cutoff)
        );
        const locEl = document.getElementById("create-class-location");
        if (locEl) {
          const loc = (r.location ?? "").trim();
          locEl.value = loc;
          locEl.title = loc;
        }
        const stEl = document.getElementById("create-class-start-time");
        const normStart = normalizeTimeForInput(
          String(r.start_time ?? "09:00")
        );
        if (stEl) stEl.value = normStart;
        setFormStartTime(normStart);
        const rec = r.recurrence_type ?? "weekly";
        document
          .querySelectorAll('input[name="create-class-recurrence"]')
          .forEach((radio) => {
            const input = radio;
            input.checked = input.value === rec;
          });
        setRecurrenceType(rec);
        // Legacy daily classes have no stored weekdays → default to every day.
        setWeekdays(
          r.recurrence_weekdays?.length
            ? weekdayStateFromPython(r.recurrence_weekdays)
            : allWeekdaysSelected()
        );
        setStartDate(parseDateField(r.recurrence_start_date));
        const parsedEnd = parseDateField(r.recurrence_end_date);
        setEndDate(parsedEnd);
        setDateRangeEnabled(Boolean(parsedEnd));
        const aut = Array.isArray(r.allowed_user_types)
          ? r.allowed_user_types
          : [];
        const amp = Array.isArray(r.allowed_membership_plans)
          ? r.allowed_membership_plans
          : Array.isArray(r.permissions?.allowed_plan_ids)
            ? r.permissions.allowed_plan_ids
            : [];

        const byPlans = amp.length > 0;

        setPressEnabled(Boolean(r.is_restricted));
        setPullEnabled(Boolean(r.pull_only));
        setUserTypes({
          push: !byPlans && aut.includes("push"),
          pull: !byPlans && aut.includes("pull"),
        });
        setMemberships(
          Object.fromEntries(plans.map(({ id }) => [id, amp.includes(id)]))
        );
        setAccessMode(byPlans ? "membership" : "user_type");
        setThumbnailFile(null);
        setThumbnailPreview(
          typeof r.thumbnail_url === "string" && r.thumbnail_url
            ? r.thumbnail_url
            : null
        );
        setSelectedCoachIds(
          Array.isArray(r.coach_ids) ? r.coach_ids.map(String) : []
        );
      } else {
        const nameEl = document.getElementById("create-class-event-name");
        if (nameEl) nameEl.value = "";
        setFormName("");
        const descEl = document.getElementById("create-class-description");
        if (descEl) descEl.value = "";
        const durEl = document.getElementById("create-class-duration");
        if (durEl) durEl.value = "60";
        setFormDuration("60");
        const capEl = document.getElementById("create-class-capacity");
        if (capEl) capEl.value = "10";
        setFormCapacity("10");
        setFormBookingCutoff("");
        const locEl = document.getElementById("create-class-location");
        if (locEl) {
          locEl.value = "";
          locEl.title = "";
        }
        const stEl = document.getElementById("create-class-start-time");
        if (stEl) stEl.value = "09:00";
        setFormStartTime("09:00");
        document
          .querySelectorAll('input[name="create-class-recurrence"]')
          .forEach((radio) => {
            const input = radio;
            input.checked = input.value === "daily";
          });
        setRecurrenceType("daily");
        setWeekdays(allWeekdaysSelected());
        setStartDate(undefined);
        setEndDate(undefined);
        setDateRangeEnabled(false);
        setPressEnabled(false);
        setPullEnabled(false);
        setAccessMode("user_type");
        setUserTypes({ push: true, pull: false });
        setMemberships(Object.fromEntries(plans.map(({ id }) => [id, false])));
        setThumbnailFile(null);
        setThumbnailPreview(null);
        setSelectedCoachIds([]);
      }
      setSubmitError(null);
    });
    return () => cancelAnimationFrame(id);
  }, [isOpen, initialRecord, plans]);

  useLayoutEffect(() => {
    if (!isOpen || initialScrollSection !== "access_permissions") return;
    let raf2 = 0;
    const raf1 = requestAnimationFrame(() => {
      raf2 = requestAnimationFrame(() => {
        accessSectionRef.current?.scrollIntoView({
          behavior: "smooth",
          block: "start",
        });
      });
    });
    return () => {
      cancelAnimationFrame(raf1);
      if (raf2) cancelAnimationFrame(raf2);
    };
  }, [isOpen, initialScrollSection]);

  const editId = initialRecord?._id ?? initialRecord?.id;
  const isEdit = Boolean(editId);

  if (!isOpen) return null;

  return (
    <div
      className="Modal-Box create-class-modal"
      role="dialog"
      aria-modal="true"
      aria-labelledby="create-class-modal-title"
    >
      <button
        type="button"
        className="overlay"
        onClick={handleClose}
        aria-label="Close modal"
      />
      <div className="box">
        <header>
          <h2 id="create-class-modal-title">
            {isEdit ? "Edit class" : "Create Class"}
          </h2>
          <button
            type="button"
            onClick={handleClose}
            className="close"
            data-button-type="tertiary"
            data-icon-only="true"
            aria-label="Close"
          >
            <img src="/img/new-icons/red-cross.svg" alt="" />
          </button>
        </header>
        <div className="body">
          <div className="create-class-modal-cols">
            <div className="create-class-modal-section-head">
              <h3 className="create-class-modal-section-title">
                Basis Details
              </h3>
              <p className="create-class-modal-section-desc">
                Define the core settings for this class session.
              </p>
            </div>
            <div className="create-class-modal-form">
              <div className="form-group">
                <label htmlFor="create-class-event-name">Event Name</label>
                <input
                  id="create-class-event-name"
                  type="text"
                  placeholder="e.g. Barbell Club"
                  aria-label="Event name"
                  onChange={(e) => setFormName(e.target.value)}
                />
              </div>
              <div className="form-group">
                <label htmlFor="create-class-description">Description</label>
                <textarea
                  id="create-class-description"
                  placeholder="Add details about the session, what to expect, requirements, or anything members should know."
                  rows={4}
                  aria-label="Description"
                />
              </div>
              <div className="create-class-modal-form-two-cols">
                <div className="form-group">
                  <div className="create-class-modal-upload-wrapper">
                    <div className="create-class-modal-upload">
                      <input
                        ref={thumbnailInputRef}
                        type="file"
                        accept="image/*"
                        className="create-class-modal-upload-input"
                        aria-label="Upload thumbnail"
                        onChange={onThumbnailPick}
                      />
                      <button
                        type="button"
                        className="create-class-modal-upload-preview-trigger"
                        onClick={() => thumbnailInputRef.current?.click()}
                      >
                        {thumbnailPreview ? (
                          <img
                            src={thumbnailPreview}
                            alt=""
                            className="create-class-modal-thumb-preview"
                          />
                        ) : (
                          <>
                            <span className="create-class-modal-upload-trigger">
                              Upload Thumbnail
                            </span>
                            <span className="create-class-modal-upload-hint">
                              Recommend size 16:9
                            </span>
                          </>
                        )}
                      </button>
                    </div>
                  </div>
                </div>
                <div className="form-group">
                  <label htmlFor="create-class-coach">Coaches</label>
                  <CoachMultiSelect
                    id="create-class-coach"
                    value={selectedCoachIds}
                    onChange={setSelectedCoachIds}
                  />
                </div>
              </div>
            </div>
          </div>

          <hr className="create-class-modal-separator" aria-hidden="true" />

          <div className="create-class-modal-cols create-class-modal-section-scheduling">
            <div className="create-class-modal-section-head">
              <h3 className="create-class-modal-section-title">Scheduling</h3>
              <p className="create-class-modal-section-desc">
                Define the core settings for this class session.
              </p>
            </div>
            <div className="create-class-modal-form">
              <div
                className="create-class-modal-recurrence"
                role="radiogroup"
                aria-label="Recurrence for the class"
                onChange={(e) => setRecurrenceType(e.target.value)}
              >
                <span className="create-class-modal-label">
                  Recurrence for the class
                </span>
                <div className="create-class-modal-recurrence-options">
                  <label className="radio-input">
                    <input
                      type="radio"
                      name="create-class-recurrence"
                      value="one_time"
                    />
                    <span className="radio" aria-hidden="true" />
                    <span className="text">One-time</span>
                  </label>
                  <label className="radio-input">
                    <input
                      type="radio"
                      name="create-class-recurrence"
                      value="daily"
                      defaultChecked
                    />
                    <span className="radio" aria-hidden="true" />
                    <span className="text">Daily</span>
                  </label>
                  <label className="radio-input">
                    <input
                      type="radio"
                      name="create-class-recurrence"
                      value="weekly"
                    />
                    <span className="radio" aria-hidden="true" />
                    <span className="text">Weekly</span>
                  </label>
                  <label className="radio-input">
                    <input
                      type="radio"
                      name="create-class-recurrence"
                      value="monthly"
                    />
                    <span className="radio" aria-hidden="true" />
                    <span className="text">Monthly</span>
                  </label>
                </div>
              </div>
              {recurrenceType === "daily" && (
                <div className="create-class-modal-weekdays-group">
                  <span className="create-class-modal-label">Repeat on</span>
                  <fieldset className="create-class-modal-weekdays">
                    <legend className="sr-only">Days of the week</legend>
                    {WEEKDAYS.map(({ key, label }) => (
                      <label key={key} className="checkbox-input">
                        <input
                          type="checkbox"
                          checked={weekdays[key]}
                          onChange={() => toggleWeekday(key)}
                        />
                        <span className="checkbox" aria-hidden="true" />
                        <span className="text">{label}</span>
                      </label>
                    ))}
                  </fieldset>
                  {dailyNeedsWeekday && (
                    <p className="create-class-modal-weekdays-error">
                      Select at least one day.
                    </p>
                  )}
                </div>
              )}
              <div className="create-class-modal-form-two-cols">
                <div className="form-group">
                  <label htmlFor="create-class-start-date">Starting Date</label>
                  <div
                    className="create-class-modal-date-dropdown"
                    ref={startPickerRef}
                  >
                    <input
                      type="hidden"
                      name="create-class-start-date"
                      value={toYYYYMMDD(startDate)}
                    />
                    <button
                      type="button"
                      id="create-class-start-date"
                      className="create-class-modal-date-trigger"
                      onClick={() =>
                        setOpenDatePicker(
                          openDatePicker === "start" ? null : "start"
                        )
                      }
                      aria-label="Starting date"
                      aria-expanded={openDatePicker === "start"}
                    >
                      <img
                        src="/img/new-icons/calendar-ico.svg"
                        alt=""
                        className="create-class-modal-date-icon-inline"
                        aria-hidden="true"
                      />
                      {formatDate(startDate) || "Select"}
                    </button>
                    <img
                      src="/img/new-icons/chevron-down.svg"
                      alt=""
                      className="create-class-modal-date-chevron"
                      aria-hidden="true"
                    />
                    {openDatePicker === "start" && (
                      <div className="create-class-modal-date-popover">
                        <DayPicker
                          mode="single"
                          selected={startDate}
                          onSelect={(date) => {
                            setStartDate(date);
                            setOpenDatePicker(null);
                          }}
                          defaultMonth={startDate || new Date()}
                          showOutsideDays
                          components={{ Caption: CreateClassCalendarCaption }}
                          modifiersClassNames={{ selected: "my-selected" }}
                          formatters={{
                            formatWeekdayName: (day) =>
                              day.toLocaleDateString("en-US", {
                                weekday: "short",
                              }),
                          }}
                        />
                      </div>
                    )}
                  </div>
                  <div className="create-class-modal-checkbox-label">
                    <input
                      type="checkbox"
                      id="create-class-set-date-range"
                      checked={dateRangeEnabled}
                      onChange={(e) => {
                        const next = e.target.checked;
                        setDateRangeEnabled(next);
                        if (!next) setEndDate(undefined);
                      }}
                    />
                    <label htmlFor="create-class-set-date-range">
                      Set date range
                    </label>
                  </div>
                </div>
                {dateRangeEnabled && (
                  <div className="form-group">
                    <label htmlFor="create-class-end-date">
                      End Date (Optional)
                    </label>
                    <div
                      className="create-class-modal-date-dropdown"
                      ref={endPickerRef}
                    >
                      <input
                        type="hidden"
                        name="create-class-end-date"
                        value={toYYYYMMDD(endDate)}
                      />
                      <button
                        type="button"
                        id="create-class-end-date"
                        className="create-class-modal-date-trigger"
                        onClick={() =>
                          setOpenDatePicker(
                            openDatePicker === "end" ? null : "end"
                          )
                        }
                        aria-label="End date (optional)"
                        aria-expanded={openDatePicker === "end"}
                      >
                        <img
                          src="/img/new-icons/calendar-ico.svg"
                          alt=""
                          className="create-class-modal-date-icon-inline"
                          aria-hidden="true"
                        />
                        {formatDate(endDate) || "Select"}
                      </button>
                      <img
                        src="/img/new-icons/chevron-down.svg"
                        alt=""
                        className="create-class-modal-date-chevron"
                        aria-hidden="true"
                      />
                      {openDatePicker === "end" && (
                        <div className="create-class-modal-date-popover">
                          <DayPicker
                            mode="single"
                            selected={endDate}
                            onSelect={(date) => {
                              setEndDate(date);
                              setOpenDatePicker(null);
                            }}
                            defaultMonth={endDate || startDate || new Date()}
                            showOutsideDays
                            components={{ Caption: CreateClassCalendarCaption }}
                            modifiersClassNames={{ selected: "my-selected" }}
                            formatters={{
                              formatWeekdayName: (day) =>
                                day.toLocaleDateString("en-US", {
                                  weekday: "short",
                                }),
                            }}
                          />
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
              <div className="create-class-modal-form-two-cols">
                <div className="form-group">
                  <label htmlFor="create-class-duration">Duration</label>
                  <CreateClassSelect
                    id="create-class-duration"
                    ariaLabel="Duration"
                    value={formDuration}
                    onChange={setFormDuration}
                    placeholder="Select"
                    options={DURATION_OPTIONS.map((mins) => ({
                      value: String(mins),
                      label: formatDurationLabel(mins),
                    }))}
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="create-class-start-time">
                    Select Start Time
                  </label>
                  <CreateClassSelect
                    id="create-class-start-time"
                    ariaLabel="Select start time"
                    value={formStartTime}
                    onChange={setFormStartTime}
                    options={START_TIME_OPTIONS}
                  />
                </div>
              </div>
              <div className="create-class-modal-form-two-cols">
                <div className="form-group">
                  <label
                    htmlFor="create-class-capacity"
                    className="create-class-modal-label-with-hint"
                  >
                    Capacity
                    <HintTooltip
                      label="Capacity help"
                      message="Maximum number of members allowed to book this class."
                    />
                  </label>
                  <input
                    type="number"
                    id="create-class-capacity"
                    aria-label="Capacity (number of members)"
                    defaultValue="10"
                    min="1"
                    placeholder="e.g. 20"
                    onChange={(e) => setFormCapacity(e.target.value)}
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="create-class-location">Location</label>
                  <input
                    id="create-class-location"
                    type="text"
                    placeholder="E.g. Training room 1st floor"
                    aria-label="Location"
                    onInput={(e) => {
                      e.currentTarget.title = e.currentTarget.value || "";
                    }}
                    onBlur={(e) => {
                      const trimmed = e.currentTarget.value.trim();
                      e.currentTarget.value = trimmed;
                      e.currentTarget.title = trimmed;
                    }}
                  />
                </div>
              </div>
              <div className="create-class-modal-form-two-cols">
                <div className="form-group">
                  <label
                    htmlFor="create-class-booking-cutoff"
                    className="create-class-modal-label-with-hint"
                  >
                    Booking cutoff
                    <HintTooltip
                      label="Booking cutoff help"
                      message="Blocks new bookings this amount of time before the event starts."
                    />
                  </label>
                  <div className="create-class-modal-select-wrapper">
                    <select
                      id="create-class-booking-cutoff"
                      value={formBookingCutoff}
                      onChange={(e) => setFormBookingCutoff(e.target.value)}
                      aria-label="Booking cutoff in hours"
                    >
                      {BOOKING_CUTOFF_OPTIONS.map((opt) => (
                        <option key={opt.value} value={opt.value}>
                          {opt.label}
                        </option>
                      ))}
                    </select>
                    <img
                      src="/img/new-icons/chevron-down.svg"
                      alt=""
                      className="create-class-modal-select-chevron"
                      aria-hidden="true"
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>

          {(showPressToggle || showPullToggle) && (
            <>
              <hr className="create-class-modal-separator" aria-hidden="true" />

              <div
                ref={accessSectionRef}
                id="scheduling-edit-modal-access-permissions"
                className="create-class-modal-cols create-class-modal-access-booking"
              >
                <div className="create-class-modal-section-head">
                  <h3 className="create-class-modal-section-title">
                    Access & Permissions
                  </h3>
                  <p className="create-class-modal-section-desc">
                    Control who can view and book this session.
                  </p>
                </div>
                <div className="create-class-modal-form scheduling-access-options-wrapper">
                  <div className="scheduling-access-options">
                    {showPressToggle && (
                      <div className="scheduling-access-row scheduling-access-row-switch scheduling-access-row-press">
                        <input
                          type="checkbox"
                          role="switch"
                          aria-label="Press users access"
                          aria-checked={pressEnabled}
                          checked={pressEnabled}
                          onChange={() => setPressEnabled(!pressEnabled)}
                        />
                        <span className="scheduling-access-row-text">
                          <span className="scheduling-access-row-title">
                            PRESS USERS{" "}
                            <span className="scheduling-access-row-title-hint">
                              (Gym Members)
                            </span>
                          </span>
                          <span className="scheduling-access-row-desc">
                            Only selected user types or membership plans can
                            view and book this session.
                          </span>
                        </span>
                      </div>
                    )}
                    {showPressToggle && pressEnabled && (
                      <>
                        <div
                          className="scheduling-access-mode"
                          role="radiogroup"
                          aria-label="Restrict access by"
                        >
                          {userTypeAvailable && (
                            <label className="radio-input">
                              <input
                                type="radio"
                                name="create-class-access-mode"
                                checked={accessModeEffective === "user_type"}
                                onChange={() => selectAccessMode("user_type")}
                              />
                              <span className="radio" aria-hidden="true" />
                              <span className="text">By user type</span>
                            </label>
                          )}
                          <label className="radio-input">
                            <input
                              type="radio"
                              name="create-class-access-mode"
                              checked={accessModeEffective === "membership"}
                              onChange={() => selectAccessMode("membership")}
                            />
                            <span className="radio" aria-hidden="true" />
                            <span className="text">By membership plans</span>
                          </label>
                        </div>
                        <div className="scheduling-access-subsections">
                          {userTypeAvailable && (
                            <section
                              className={`scheduling-access-subsection${
                                accessModeEffective !== "user_type"
                                  ? " is-disabled"
                                  : ""
                              }`}
                              aria-disabled={
                                accessModeEffective !== "user_type"
                              }
                            >
                              <h3 className="scheduling-access-subsection-title">
                                User type
                              </h3>
                              <div className="scheduling-access-subsection-body">
                                {visibleUserTypes.map(({ id, label }) => (
                                  <label key={id} className="checkbox-input">
                                    <input
                                      type="checkbox"
                                      checked={userTypes[id]}
                                      disabled={
                                        accessModeEffective !== "user_type"
                                      }
                                      onChange={() => toggleUserType(id)}
                                    />
                                    <span
                                      className="checkbox"
                                      aria-hidden="true"
                                    />
                                    <span className="text">{label}</span>
                                  </label>
                                ))}
                              </div>
                            </section>
                          )}
                          <section
                            className={`scheduling-access-subsection${
                              accessModeEffective !== "membership"
                                ? " is-disabled"
                                : ""
                            }`}
                            aria-disabled={accessModeEffective !== "membership"}
                          >
                            <div className="scheduling-access-subsection-header">
                              <h3 className="scheduling-access-subsection-title">
                                Memberships
                              </h3>
                              <button
                                type="button"
                                className="scheduling-settings-all"
                                disabled={accessModeEffective !== "membership"}
                                onClick={() =>
                                  setAllMemberships(!allMembershipsChecked)
                                }
                                aria-pressed={allMembershipsChecked}
                              >
                                <img
                                  src={
                                    allMembershipsChecked
                                      ? "/img/new-icons/checked-square.svg"
                                      : "/img/new-icons/unchecked-square.svg"
                                  }
                                  alt=""
                                  aria-hidden="true"
                                />
                                <span>All</span>
                              </button>
                            </div>
                            <div className="scheduling-access-subsection-body">
                              {plans.map(({ id, label }) => (
                                <label key={id} className="checkbox-input">
                                  <input
                                    type="checkbox"
                                    checked={memberships[id] ?? false}
                                    disabled={
                                      accessModeEffective !== "membership"
                                    }
                                    onChange={() => toggleMembership(id)}
                                  />
                                  <span
                                    className="checkbox"
                                    aria-hidden="true"
                                  />
                                  <span className="text">{label}</span>
                                </label>
                              ))}
                            </div>
                          </section>
                        </div>
                      </>
                    )}
                    {showPullToggle && (
                      <div className="scheduling-access-row scheduling-access-row-switch scheduling-access-row-pull">
                        <input
                          type="checkbox"
                          role="switch"
                          aria-label="Pull users access"
                          aria-checked={pullEnabled}
                          checked={pullEnabled}
                          onChange={() => setPullEnabled(!pullEnabled)}
                        />
                        <span className="scheduling-access-row-text">
                          <span className="scheduling-access-row-title">
                            PULL USERS{" "}
                            <span className="scheduling-access-row-title-hint">
                              (1:1 training)
                            </span>
                          </span>
                          <span className="scheduling-access-row-desc">
                            Only PULL users can view and book this session.
                          </span>
                        </span>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </>
          )}

          {/* <div className="create-class-modal-cols create-class-modal-access-booking">
            <div className="create-class-modal-section-head">
              <h3 className="create-class-modal-section-title">Booking Limits</h3>
              <p className="create-class-modal-section-desc">
                Limit how often members can book this session.
              </p>
            </div>
            <div className="create-class-modal-form">
              <div className="scheduling-booking-limits-blocks">
                <div className="scheduling-booking-limit-block">
                  <div className="scheduling-access-row scheduling-access-row-switch">
                    <input
                      type="checkbox"
                      role="switch"
                      aria-label="Per time period"
                      aria-checked={perTimePeriodEnabled}
                      checked={perTimePeriodEnabled}
                      onChange={() => setPerTimePeriodEnabled(!perTimePeriodEnabled)}
                    />
                    <span className="scheduling-access-row-text">
                      <span className="scheduling-access-row-title">Per Time Period</span>
                      <span className="scheduling-access-row-desc">
                        Limit how many bookings can be made within a selected time period for this event.
                      </span>
                    </span>
                  </div>
                  {perTimePeriodEnabled && (
                    <div className="scheduling-booking-limit-cols">
                      <div className="scheduling-booking-limit-col">
                        <span className="scheduling-booking-limit-label">Select time period</span>
                        <div className="scheduling-booking-limit-radios" role="radiogroup" aria-label="Time period">
                          {TIME_PERIOD_OPTIONS.map(({ id, label }) => (
                            <label key={id} className="radio-input">
                              <input
                                type="radio"
                                name="create-class-booking-limit-time-period"
                                value={id}
                                checked={perTimePeriodPeriod === id}
                                onChange={() => setPerTimePeriodPeriod(id)}
                              />
                              <span className="radio" aria-hidden="true" />
                              <span className="text">{label}</span>
                            </label>
                          ))}
                        </div>
                      </div>
                      <div className="scheduling-booking-limit-col scheduling-booking-limit-max">
                        <span className="scheduling-booking-limit-label">
                          Max sessions per {perTimePeriodPeriod === "daily" ? "day" : perTimePeriodPeriod === "weekly" ? "week" : "month"}
                        </span>
                        <select
                          className="scheduling-booking-limit-select"
                          value={perTimePeriodMax}
                          onChange={(e) => setPerTimePeriodMax(Number(e.target.value))}
                          aria-label="Max sessions per time period"
                        >
                          {MAX_SESSIONS_OPTIONS.map((n) => (
                            <option key={n} value={n}>
                              {n}
                            </option>
                          ))}
                        </select>
                      </div>
                    </div>
                  )}
                </div>
                <div className="scheduling-booking-limit-block">
                  <div className="scheduling-access-row scheduling-access-row-switch">
                    <input
                      type="checkbox"
                      role="switch"
                      aria-label="Per member"
                      aria-checked={perMemberEnabled}
                      checked={perMemberEnabled}
                      onChange={() => setPerMemberEnabled(!perMemberEnabled)}
                    />
                    <span className="scheduling-access-row-text">
                      <span className="scheduling-access-row-title">Per Member</span>
                      <span className="scheduling-access-row-desc">
                        Set individual booking limits for each member for this event.
                      </span>
                    </span>
                  </div>
                  {perMemberEnabled && (
                    <div className="scheduling-booking-limit-cols">
                      <div className="scheduling-booking-limit-col">
                        <span className="scheduling-booking-limit-label">Select time period</span>
                        <div className="scheduling-booking-limit-radios" role="radiogroup" aria-label="Per member time period">
                          {TIME_PERIOD_OPTIONS.map(({ id, label }) => (
                            <label key={id} className="radio-input">
                              <input
                                type="radio"
                                name="create-class-booking-limit-per-member-period"
                                value={id}
                                checked={perMemberPeriod === id}
                                onChange={() => setPerMemberPeriod(id)}
                              />
                              <span className="radio" aria-hidden="true" />
                              <span className="text">{label}</span>
                            </label>
                          ))}
                        </div>
                      </div>
                      <div className="scheduling-booking-limit-col scheduling-booking-limit-max">
                        <span className="scheduling-booking-limit-label">
                          Max sessions per member per {perMemberPeriod === "daily" ? "day" : perMemberPeriod === "weekly" ? "week" : "month"}
                        </span>
                        <select
                          className="scheduling-booking-limit-select"
                          value={perMemberMax}
                          onChange={(e) => setPerMemberMax(Number(e.target.value))}
                          aria-label="Max sessions per member per time period"
                        >
                          {MAX_SESSIONS_OPTIONS.map((n) => (
                            <option key={n} value={n}>
                              {n}
                            </option>
                          ))}
                        </select>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div> */}

          {submitError && (
            <p className="create-class-modal-error" role="alert">
              {submitError}
            </p>
          )}
          <footer className="scheduling-classes-modal-footer">
            <button
              type="button"
              data-button-type="outline"
              onClick={handleClose}
              disabled={isSubmitting}
            >
              Cancel
            </button>
            <button
              type="button"
              data-button-type="primary"
              onClick={handleCreateClass}
              disabled={isSubmitting || !isFormValid}
            >
              {isSubmitting ? "Saving…" : "Save"}
            </button>
          </footer>
        </div>
      </div>
    </div>
  );
};

export default CreateClassModal;
